import React, { useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, AlertTriangle, Smartphone } from "lucide-react";

const isMobileDevice = () =>
  typeof navigator !== "undefined" &&
  /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Opera Mini/i.test(navigator.userAgent);

const isIOS = () => typeof navigator !== "undefined" && /iP(hone|ad|od)/i.test(navigator.userAgent);

export function SmsButton({ mobile, message, variant = "ghost", size = "icon", testId = "sms", label }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(message || "");

  const digits = String(mobile || "").replace(/\D/g, "");
  const validNumber = digits.length >= 10;
  const mobileDevice = isMobileDevice();

  const onOpen = () => { setText(message || ""); setOpen(true); };

  const openSmsApp = () => {
    const sep = isIOS() ? "&" : "?";
    window.location.href = `sms:${digits}${sep}body=${encodeURIComponent(text)}`;
  };

  return (
    <>
      <Button variant={variant} size={size} onClick={onOpen} title={t("sms.send")} data-testid={`${testId}-btn`}>
        <MessageSquare className="h-4 w-4" />
        {size !== "icon" && <span className="ml-1.5">{label || t("sms.send")}</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="sms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("sms.preview")}</DialogTitle>
            <DialogDescription>{t("sms.note")}</DialogDescription>
          </DialogHeader>

          {!mobileDevice ? (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3" data-testid="sms-desktop-note">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p className="text-sm">{t("sms.desktop")}</p>
            </div>
          ) : !validNumber ? (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3" data-testid="sms-invalid-note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{t("sms.invalid")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{t("sms.to")}: <span className="font-medium text-foreground tnum">{digits}</span></div>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="sms-message-input" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            {mobileDevice && validNumber && (
              <Button onClick={openSmsApp} data-testid="sms-open-app-btn"><MessageSquare className="mr-1.5 h-4 w-4" />{t("sms.openApp")}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
